import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { useTranslate } from '@repo/unicity'
import { useAtomValue } from 'jotai'
import CheckoutLayoutSkeleton from '@/core/checkout/components/checkout-layout-skeleton.component'
import CartEmpty from '@/core/cart/CartEmpty'
import { LoginBar } from '@/core/components'
import { cartEditedAtom } from '@/core/cart/cart.store'
import { v2CartAtom } from '@/core/cart/cart.store'
import { useOmanCheckout } from '../../hooks/useOmanCheckout'
import { useNavigateToShipping } from '../payments/hooks/useNavigateToShipping'
import { useOmanCheckoutInit } from './hooks/useOmanCheckoutInit'
import { CheckoutLayoutGrid } from './checkout.styles'
import PreSummaryBox from './components/PreSummaryBox'
import CheckoutStepper from './components/CheckoutStepper'
import GuestCreateAccountStep from './components/GuestCreateAccountStep'
import MemberShippingStep from './components/MemberShippingStep'

const OmanCheckoutModule = () => {
    const { translate } = useTranslate()
    const checkout = useOmanCheckout()
    const { initialized, cartActions, route, isLoadingFormat, isLoadingGeo, formatData, errors, retryCheckout } = useOmanCheckoutInit(checkout)
    // `route` is URL-driven (create-account = guest, shipping = member). Logging in
    // via the LoginBar must move to /checkout/shipping, or the guest form persists.
    const goToShipping = useNavigateToShipping()
    const cartEdited = useAtomValue(cartEditedAtom)
    const v2Cart = useAtomValue(v2CartAtom)

    const showSkeleton = !initialized || isLoadingFormat || isLoadingGeo
    const hasInitError = !initialized && !checkout.loading && !isLoadingFormat && !isLoadingGeo && errors.length > 0

    if (showSkeleton) {
        if (hasInitError) {
            return (
                <Stack
                    spacing={2}
                    alignItems="center"
                    py={8}
                    maxWidth={480}
                    mx="auto"
                    px={{ xs: '20px', sm: 3 }}
                    sx={{ boxSizing: 'border-box' }}
                    data-testid="checkout-error-stack">
                    <Alert severity="error" sx={{ width: '100%' }} data-testid="checkout-error-alert">
                        {translate('unable_to_initialize_checkout')}
                    </Alert>
                    <Button variant="contained" onClick={retryCheckout} data-testid="checkout-retry-btn">
                        {translate('retry')}
                    </Button>
                </Stack>
            )
        }
        return <CheckoutLayoutSkeleton data-testid="checkout-skeleton" />
    }

    const itemsLength = formatData?.items?.length ?? 0
    if (cartEdited && itemsLength === 0 && v2Cart.length === 0) {
        return <CartEmpty data-testid="checkout-cart-empty" />
    }

    return (
        <>
            <CheckoutStepper activeStep="shipping" data-testid="checkout-stepper" />
            <CheckoutLayoutGrid data-testid="checkout-layout-grid">
                {route === 'create-account' && (
                    <Box sx={{ gridColumn: { lg: '1' }, gridRow: { lg: '1' } }} data-testid="checkout-login-bar-box">
                        <LoginBar onLoginSuccess={() => goToShipping()} data-testid="checkout-login-bar" />
                    </Box>
                )}
                <Box sx={{ gridColumn: { lg: '2' }, gridRow: { lg: '1 / span 2' } }} data-testid="checkout-summary-box">
                    <PreSummaryBox cartActions={cartActions} data-testid="checkout-pre-summary" />
                </Box>
                <Stack
                    spacing={4}
                    sx={{
                        gridColumn: { lg: '1' },
                        gridRow: { lg: route === 'create-account' ? '2' : '1 / span 2' }
                    }}
                    data-testid="checkout-form-stack">
                    {route === 'shipping' ? (
                        <MemberShippingStep data-testid="checkout-member-shipping" />
                    ) : (
                        <GuestCreateAccountStep data-testid="checkout-guest-create-account" />
                    )}
                </Stack>
            </CheckoutLayoutGrid>
        </>
    )
}

export default OmanCheckoutModule
